'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @primitive SkeletonGroup (client)
 * ROLE: none on wrapper (aria-busy="true" communicates loading to AT).
 *   A visually-hidden <span role="status">{label}</span> provides an immediate
 *   polite announcement on mount. The span is removed after announcement so
 *   subsequent re-renders do not re-announce (anti-thrash discipline).
 * KEYBOARD: non-interactive wrapper; children may be interactive.
 * FOCUS: none — does not move focus.
 * LIVE-REGION: visually-hidden <span role="status"> fires once on mount, then
 *   is removed from the live region. aria-busy on the wrapper persists until
 *   unmount. On unmount, AT observes the aria-busy removal and the skeleton
 *   children disappearing, signalling load completion.
 * PRM: children (Skeleton/SkeletonText) handle their own PRM rules.
 * FORCED-COLORS: transparent; children handle forced-colors.
 * RTL: direction-agnostic
 * RSC: CLIENT (needs useEffect for announcement cleanup)
 */
import { useEffect, useRef, useState } from 'react';
import styles from './SkeletonGroup.module.css';
export function SkeletonGroup({ label, children, className }) {
    // Show the live-region span briefly on mount, then remove it to prevent
    // re-announcement on parent re-renders.
    const [showAnnouncement, setShowAnnouncement] = useState(true);
    const announcedRef = useRef(false);
    useEffect(() => {
        if (!announcedRef.current) {
            announcedRef.current = true;
            // Allow AT to read the announcement before removing it.
            const id = setTimeout(() => setShowAnnouncement(false), 500);
            return () => clearTimeout(id);
        }
        return undefined;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const rootCls = [styles.root, className].filter(Boolean).join(' ');
    return (_jsxs("div", { "aria-busy": "true", className: rootCls, children: [showAnnouncement && (_jsx("span", { role: "status", className: styles.srOnly, children: label })), children] }));
}
//# sourceMappingURL=SkeletonGroup.js.map